import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../../utils/tokenManager";

/**
 * Extracted from admin/Assignment.jsx - same fetch of clients/agents,
 * same validation, same PUT payload, same endpoint. Only the "edit
 * client" secondary modal (a separate concern on desktop) is left out -
 * this hook covers just the assignment itself.
 */
export function useAssignClient() {
  const { t } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ clientId: "", agentId: "" });
  const [errors, setErrors] = useState({});
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      const accessToken = await getValidToken();
      try {
        const response = await axios.request({
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/clients`,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setClients(response.data.clients.map((client) => ({ label: `${client.Name} (ID: ${client.Id})`, value: client.Id })));
      } catch (error) {
        setErrorModal(true);
      }
    };

    const fetchAgents = async () => {
      const accessToken = await getValidToken();
      try {
        const response = await axios.request({
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/users`,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setAgents(response.data.users.map((agent) => ({ label: agent.FullName, value: agent.Id, ...agent })));
      } catch (error) {
        setErrorModal(true);
      }
    };

    fetchClients();
    fetchAgents();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!form.clientId) newErrors.clientId = t("validation.required");
    if (!form.agentId) newErrors.agentId = t("validation.required");
    return newErrors;
  };

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const selectedAgent = agents.find((agent) => String(agent.value) === String(form.agentId));
      const selectedClient = clients.find((client) => String(client.value) === String(form.clientId));

      const updateData = {
        ClientId: form.clientId,
        Email: selectedAgent?.Email,
        FullName: selectedAgent?.FullName,
        Phone: selectedAgent?.Phone,
        Role: selectedAgent?.Role,
        Workshift: selectedAgent?.Workshift,
      };

      const accessToken = await getValidToken();
      await axios.request({
        method: "put",
        url: `${process.env.REACT_APP_APIBASEURL}/api/users/${form.agentId}`,
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        data: updateData,
      });

      const currentSessionAgentId = sessionStorage.getItem("agentId");
      if (currentSessionAgentId && String(form.agentId) === String(currentSessionAgentId)) {
        try {
          if (selectedClient?.label) sessionStorage.setItem("clientName", selectedClient.label);
          if (selectedClient?.value) sessionStorage.setItem("clinentId", String(selectedClient.value));
        } catch (_) {}
        try {
          window.dispatchEvent(new Event("client-assigned"));
        } catch (_) {}
      }

      setSuccessModal(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
    }
  };

  return {
    agents,
    clients,
    form,
    errors,
    successModal,
    setSuccessModal,
    errorModal,
    setErrorModal,
    handleChange,
    handleSubmit,
  };
}